import React, { useState } from 'react'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm as useHookForm } from 'react-hook-form'
import useSWRMutation from 'swr/mutation'
import type { KeyedMutator } from 'swr'
import { PlusIcon } from '@radix-ui/react-icons'
import { formDataFetcher } from 'api/swr'
import MyPagination from 'components/ui/MyPagination'
import MyRefetchButton from 'components/ui/MyRefetchButton'
import { Button } from '@/components/ui/button'
import { Form } from '@/components/ui/form'
import Loader from '@/components/ui/Loader'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useForm } from './index'
import { MyFormInput } from 'components/ui/MyFormField'
import type { TempSpineZip } from './types'

const createSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  autoDeleteDays: z.coerce.number().int().min(0),
})

type CreateFormValues = z.infer<typeof createSchema>

interface DataTableToolbarProps {
  mutate: KeyedMutator<TempSpineZip[]>
  nextAfter?: string | null
}

const DataTableToolbar: React.FC<DataTableToolbarProps> = ({
  mutate,
  nextAfter,
}) => {
  const afters = useForm((s) => s.afters)
  const after = useForm((s) => s.after)
  const goAfter = useForm((s) => s.goAfter)

  return (
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-2">
        <MyPagination
          nextAfter={nextAfter}
          after={after}
          goAfter={goAfter}
          afters={afters}
        />
        <MyRefetchButton onClick={() => mutate()} />
      </div>
      <DataTableRowCreate mutate={mutate} />
    </div>
  )
}

interface DataTableRowCreateProps {
  mutate: KeyedMutator<TempSpineZip[]>
}

export const DataTableRowCreate: React.FC<DataTableRowCreateProps> = ({
  mutate,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [zipFile, setZipFile] = useState<File | null>(null)
  const { trigger, isMutating, error } = useSWRMutation(
    '/ops-tools/temp-spine-zips',
    formDataFetcher,
  )

  const form = useHookForm<CreateFormValues>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      name: '',
      description: '',
      autoDeleteDays: 0,
    },
  })

  const onSubmit = (data: CreateFormValues) => {
    if (isMutating || !zipFile) return
    const formData = new FormData()
    formData.append('zip', zipFile, zipFile.name)
    formData.append('name', data.name)
    formData.append('description', data.description ?? '')
    formData.append('autoDeleteDays', String(data.autoDeleteDays))

    trigger(formData, {
      onSuccess: () => {
        setIsOpen(false)
        setZipFile(null)
        form.reset()
        void mutate()
      },
    })
  }

  return (
    <div className="max-w-[500px] min-w-[130px] flex flex-col gap-2">
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open)
          if (!open) {
            setZipFile(null)
            form.reset()
          }
        }}
      >
        <DialogTrigger asChild>
          <Button>
            <PlusIcon className="mr-2 h-4 w-4" />
            Upload temp spine zip
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload temp spine zip</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <MyFormInput name="name" form={form} label="Name" />
              <MyFormInput name="description" form={form} label="Description" />
              <MyFormInput
                name="autoDeleteDays"
                form={form}
                label="Auto delete (days, 0 = never)"
                type="number"
              />
              <div className="grid w-full items-center gap-1.5">
                <Label htmlFor="zip">Spine zip file</Label>
                <Input
                  id="zip"
                  type="file"
                  accept=".zip"
                  onChange={(e) => setZipFile(e.target.files?.[0] ?? null)}
                />
                {zipFile ? (
                  <p className="text-sm text-muted-foreground">{zipFile.name}</p>
                ) : null}
              </div>

              {isMutating ? (
                <Loader>Uploading...</Loader>
              ) : (
                <Button type="submit" disabled={!zipFile}>
                  Upload
                </Button>
              )}
              {error ? (
                <div className="text-red-500 text-sm">{error.message}</div>
              ) : null}
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default DataTableToolbar
